<script setup lang="ts">
/**
 * Панель подразделов — второй уровень навигации. Закреплённый экземпляр стоит
 * в потоке рядом с рейлом и раздвигает контент (`pinned`/`collapsed`); preview —
 * отдельный экземпляр поверх контента, выезжающий из-под рейла (анимацию входа
 * и выхода задаёт родитель через `<Transition>`).
 */
import { computed } from 'vue';
import { RouterLink } from 'vue-router';

import { IconChevronLeft, IconChevronRight } from '@/shared/ui/icons';

import type { MenuGroup } from '../model/menu-types';
import type { PanelMode } from '../model/sidebar-panel-state';

import { useMenuBadges } from '../model/use-menu-badges';
import { useMenuNavigation } from '../model/use-menu-navigation';

interface Props {
  group: MenuGroup;
  mode: PanelMode;
}

const props = defineProps<Props>();

// Клик по шеврону: у закреплённой панели — свернуть/развернуть, у preview — закрыть.
const emit = defineEmits<{
  toggle: [];
}>();

const isCollapsed = computed(() => props.mode === 'collapsed');

const { badgeCount, badgeLabel, formatBadgeCount } = useMenuBadges(() => [props.group]);

const { isLinkActive, handleLinkClick } = useMenuNavigation();
</script>

<template>
  <div
    :id="`app-sidebar-panel-${props.group.id}`"
    class="panel"
    :class="{ 'panel--preview': props.mode === 'preview' }"
    :data-group-id="props.group.id"
  >
    <div
      class="panel__scroll"
      :class="{ 'panel__scroll--collapsed': isCollapsed }"
    >
      <div
        v-show="!isCollapsed"
        class="panel__body"
      >
        <p class="panel__title">{{ props.group.label }}</p>

        <nav :aria-label="props.group.label">
          <RouterLink
            v-for="child in props.group.children"
            :key="child.id"
            v-slot="{ href, navigate }"
            :to="child.to"
            custom
          >
            <a
              :href="href"
              class="panel__item"
              :class="{ 'panel__item--active': isLinkActive(child) }"
              :aria-current="isLinkActive(child) ? 'page' : undefined"
              @click="handleLinkClick(child, navigate, $event)"
            >
              {{ child.label }}
              <span
                v-if="badgeCount(child) > 0"
                class="ml-auto rounded-full bg-(--accent) px-2 text-xs text-(--on-accent) tabular-nums"
                :aria-label="badgeLabel(child)"
                >{{ formatBadgeCount(badgeCount(child)) }}</span
              >
            </a>
          </RouterLink>
        </nav>
      </div>
    </div>

    <button
      type="button"
      class="panel__toggle"
      :aria-label="isCollapsed ? 'Развернуть меню' : 'Свернуть меню'"
      :aria-expanded="!isCollapsed"
      :aria-controls="`app-sidebar-panel-${props.group.id}`"
      @click="emit('toggle')"
    >
      <IconChevronRight
        v-if="isCollapsed"
        :width="7"
        :height="12"
      />
      <IconChevronLeft
        v-else
        :width="7"
        :height="12"
      />
    </button>
  </div>
</template>

<style scoped>
@reference "../../../tailwind.css";

.panel {
  position: relative;
  height: 100%;
  /* Кнопка сворачивания висит на правой кромке и выходит за границы панели. */
  overflow: visible;
}

/* Preview: оверлей поверх контента — ширина панели в смещение контента не входит. */
.panel--preview {
  position: absolute;
  top: 0;
  bottom: 0;
  left: var(--sidebar-rail-width);
  width: var(--sidebar-panel-width);
  height: auto;
}

.panel__scroll {
  height: 100%;
  width: var(--sidebar-panel-width);
  padding: 24px 8px 0;
  overflow: hidden;
  background: var(--surface-card);
  border-right: 1px solid var(--border);
  transition:
    width var(--sidebar-panel-duration) var(--sidebar-panel-easing),
    padding-inline var(--sidebar-panel-duration) var(--sidebar-panel-easing);
}

.panel__scroll--collapsed {
  width: var(--sidebar-panel-collapsed-width);
  padding-inline: 0;
  border-right-width: 0;
}

/* Фиксированная ширина содержимого: при раскрытии оно не переверстывается
   вслед за анимацией ширины панели. */
.panel__body {
  width: 192px;
}

.panel__title {
  padding-left: 16px;
  margin-bottom: 8px;
  color: var(--text-primary);
  @apply text-lg;
  font-weight: 600;
}

.panel__item {
  display: flex;
  align-items: center;
  min-height: 44px;
  padding: 12px 16px;
  border-radius: 8px;
  color: var(--text-primary);
  @apply text-sm;
  font-weight: 500;
  text-decoration: none;
  overflow-wrap: anywhere;
  transition: background-color 0.2s ease;
}

.panel__item:hover {
  background: var(--surface-hover);
}

.panel__item--active,
.panel__item--active:hover {
  background: var(--surface-selected);
}

.panel__item:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: -2px;
}

.panel__toggle {
  position: absolute;
  top: 18px;
  right: -18px;
  /* При нулевой ширине панели кнопка пересекает рейл и должна быть выше него. */
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 999px;
  background: var(--surface-card);
  border: 1px solid var(--border);
  color: var(--text-secondary);
  cursor: pointer;
  transition:
    background-color 0.2s ease,
    color 0.2s ease;
}

/* У preview своя кнопка; закреплённую скрываем до конца его выходной анимации. */
.panel:not(.panel--preview):has(~ .panel--preview) .panel__toggle {
  visibility: hidden;
}

.panel__toggle:hover {
  background: var(--surface-hover);
  color: var(--text-primary);
}

.panel__toggle:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}
</style>
